import { useCallback, useRef, useState } from 'react';

export type GuardResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: 'concurrent' }
  | { ok: false; reason: 'error'; error?: unknown };

export function useRunGuards(options?: { autoClear?: boolean }) {
  const autoClear = options?.autoClear ?? false;
  const execInFlightRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const clearGuards = useCallback(() => {
    execInFlightRef.current = false;
    setIsProcessing(false);
  }, []);

  const runWithGuards = useCallback(async <T,>(task: () => Promise<T>): Promise<GuardResult<T>> => {
    if (execInFlightRef.current) return { ok: false, reason: 'concurrent' } as const;
    execInFlightRef.current = true;
    setIsProcessing(true);
    try {
      const value = await task();
      return { ok: true as const, value };
    } catch (error) {
      return { ok: false as const, reason: 'error', error };
    } finally {
      if (autoClear) {
        execInFlightRef.current = false;
        setIsProcessing(false);
      }
    }
  }, [autoClear]);

  return { runWithGuards, execInFlightRef, isProcessing, setIsProcessing, clearGuards };
}


